import { Button } from '@advanza/ui'
import NiceModal, { useModal } from '@ebay/nice-modal-react'
import TextAreaField from '@trustoo/components/input/TextAreaField'
import BaseModal, {
    BaseModalFooter,
    BaseModalHeader,
    BaseModalMainContent,
} from '@trustoo/components/modal/BaseModal'
import { QuestionAnswer } from '@trustoo/types/entity/anonymizedRequest'
import { saveAnonymizedRequest } from 'actions/anonymizedRequests'
import { useAppDispatch, useAppSelector } from 'hooks/hooks'
import { useState } from 'react'

interface AnonymizedRequestEditModalProps {
    anonymizedRequestId: number
}

const AnonymizedRequestEditModal = NiceModal.create(
    ({ anonymizedRequestId }: AnonymizedRequestEditModalProps) => {
        const anonymizedRequest = useAppSelector(
            (state) => state.anonymizedRequests.entities.anonymizedRequests[anonymizedRequestId],
        )
        if (!anonymizedRequest) {
            throw new Error('Anonymized request not found')
        }
        const modal = useModal()
        const dispatch = useAppDispatch()

        const [details, setDetails] = useState(anonymizedRequest?.details ?? '')
        const [questionsAnswers, setQuestionsAnswers] = useState<QuestionAnswer[]>(
            anonymizedRequest?.questions_answers ?? [],
        )
        const [isSaving, setIsSaving] = useState(false)

        const hasEmptyDetails = details.trim() === ''
        const hasEmptyAnswer = questionsAnswers.some(
            (questionAnswer) => questionAnswer.answer.trim() === '',
        )
        const isTouched =
            details !== anonymizedRequest.details ||
            questionsAnswers.some(
                (questionAnswer, index) =>
                    questionAnswer.answer !== anonymizedRequest.questions_answers[index]?.answer,
            )

        const onChangeAnswer = (index: number, answer: string) => {
            setQuestionsAnswers((prev) =>
                prev.map((questionAnswer, i) =>
                    i === index ? { ...questionAnswer, answer } : questionAnswer,
                ),
            )
        }

        const onSave = async () => {
            setIsSaving(true)
            try {
                await dispatch(
                    saveAnonymizedRequest(anonymizedRequestId, {
                        details,
                        questions_answers: questionsAnswers,
                    }),
                )
                modal.hide()
            } finally {
                setIsSaving(false)
            }
        }

        return (
            <BaseModal>
                <BaseModalHeader>
                    <h3>Edit anonymized request</h3>
                </BaseModalHeader>
                <BaseModalMainContent className="flex flex-col gap-4">
                    <TextAreaField
                        rows={10}
                        legend="Details"
                        value={details}
                        error={hasEmptyDetails ? 'Details cannot be empty' : undefined}
                        onChange={(e) => setDetails(e.target.value)}
                    />
                    {questionsAnswers.map((questionAnswer, index) => (
                        <div key={questionAnswer.question}>
                            <div className="mb-1 font-bold">{questionAnswer.question}</div>
                            <TextAreaField
                                value={questionAnswer.answer}
                                error={
                                    questionAnswer.answer.trim() === ''
                                        ? 'Answer cannot be empty'
                                        : undefined
                                }
                                onChange={(e) => onChangeAnswer(index, e.target.value)}
                            />
                        </div>
                    ))}
                </BaseModalMainContent>
                <BaseModalFooter className="flex items-center justify-end gap-2">
                    <Button name="text" onClick={() => modal.hide()} disabled={isSaving}>
                        Cancel
                    </Button>
                    <Button
                        appearance="primary"
                        onClick={onSave}
                        disabled={!isTouched || hasEmptyDetails || hasEmptyAnswer || isSaving}>
                        {isSaving ? 'Saving..' : 'Save'}
                    </Button>
                </BaseModalFooter>
            </BaseModal>
        )
    },
)

export default AnonymizedRequestEditModal
